import React, { useEffect, useMemo, useState } from "react";

import moment from "moment";

// Common
import SnackMessage from "react-lib/apps/common/SnackMessage";
import { ADMIN_NAME_LIST } from "react-lib/apps/USER/CardSettingDivision";

import CardCourseList from "./CardCourseList";
import CardEmployeeLeaveRequest from "./CardEmployeeLeaveRequest";
import CardEmployeeSummaryWorkHour from "./CardEmployeeSummaryWorkHour";
import CardEmployeeTimeAttendance from "./CardEmployeeTimeAttendance";
// Component
import CardSetupWorkSchedule from "./CardSetupWorkSchedule";
import CardSetupWorkShiftsTemplate from "./CardSetupWorkShiftsTemplate";
import CardTrainingManagement from "./CardTrainingManagement";
// UX
import CardWorkScheduleUX from "./CardWorkScheduleUX";

// Interface
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  TAB_MENU_MAPPING,
  TabMenu,
} from "./sequence/WorkSchedule";

// Utils
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";

// Type
type CardWorkScheduleProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  CourseListSequence: any;
  EmployeeSWHSequence: any;
  EmployeeTASequence: any;
  SetupWorkScheduleSequence: any;
  TrainingManagementSequence: any;
  WorkScheduleSequence: State["WorkScheduleSequence"];
  buttonLoadCheck: any;
  django: any;
  masterData: any;
  searchedItemListWithKey: any;
  // options
  languageUX: any;
} & PickedProps;

const getDefaultFilterDate = () => {
  const startDate = moment().startOf("month");
  const endDate = moment().endOf("month");

  return {
    start_date: dateToStringWithoutTimeBE(startDate),
    end_date: dateToStringWithoutTimeBE(endDate),
  };
};

const CardWorkSchedule = (props: CardWorkScheduleProps) => {
  const [activeTab, setActiveTab] = useState<"" | TabMenu>("");

  const filteredTabMenu = useMemo(() => {
    const tabs = props.adminMenuDivisions?.all[ADMIN_NAME_LIST["Work Schedule"]]?.tabs ?? [];

    return Object.entries(TAB_MENU_MAPPING)
      .filter(([key]) => tabs.includes(key))
      .map((menu) => menu[1]);
  }, [props.adminMenuDivisions]);

  useEffect(() => {
    props.runSequence({ sequence: "WorkSchedule", restart: true });
  }, []);

  useEffect(() => {
    setActiveTab(filteredTabMenu[0] || "");
  }, [filteredTabMenu]);

  const handleChangeTab = (tab: TabMenu) => {
    if (tab === activeTab) {
      return;
    }

    setActiveTab(tab);

    const divisionMenuIds =
      props.adminMenuDivisions?.all[ADMIN_NAME_LIST["Work Schedule"]]?.ids ?? {};
    const divisionId = divisionMenuIds[props.selectedDivision?.id] || null;
    const allDivisionIds = Object.values(divisionMenuIds);

    props.runSequence({
      sequence: "WorkSchedule",
      menu: tab,
      action: "FETCH",
      ...(tab === "LeaveRequest"
        ? {
            filter: {
              ...getDefaultFilterDate(),
              affiliate: divisionId,
              affiliate_list: allDivisionIds,
            },
          }
        : {}),
    });
  };

  const divisionOptions = useMemo(
    () => props.adminMenuDivisions?.all[ADMIN_NAME_LIST["Work Schedule"]]?.divisions || [],
    [props.adminMenuDivisions, props.selectedDivision]
  );

  const displayTab = useMemo(() => {
    switch (activeTab) {
      case "SetupSchedule":
        return (
          <CardSetupWorkSchedule
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            SetupWorkScheduleSequence={props.SetupWorkScheduleSequence}
            adminMenuDivisions={props.adminMenuDivisions}
            buttonLoadCheck={props.buttonLoadCheck}
            errorMessage={props.errorMessage}
            searchedItemListWithKey={props.searchedItemListWithKey}
            selectedDivision={props.selectedDivision}
            divisionOptions={divisionOptions}
          />
        );
      case "SetupShiftsTemplate":
        return (
          <CardSetupWorkShiftsTemplate
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            setupWorkShiftsTemplate={props.WorkScheduleSequence?.setupWorkShiftsTemplate}
            buttonLoadCheck={props.buttonLoadCheck}
            errorMessage={props.errorMessage}
            successMessage={props.successMessage}
            masterOptions={props.masterOptions}
            searchedItemListWithKey={props.searchedItemListWithKey}
          />
        );
      case "LeaveRequest":
        return (
          <CardEmployeeLeaveRequest
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            employeeLeaveRequest={props.WorkScheduleSequence?.employeeLeaveRequest}
            buttonLoadCheck={props.buttonLoadCheck}
            masterData={props.masterData}
            masterOptions={props.masterOptions}
            searchedItemListWithKey={props.searchedItemListWithKey}
            divisionOptions={divisionOptions}
          />
        );
      case "EmployeeTraining": {
        return (
          <CardCourseList
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            CourseListSequence={props.CourseListSequence}
            hideDeleteCourse
            hideNewCourse
            readOnly
            buttonLoadCheck={props.buttonLoadCheck}
            django={props.django}
            errorMessage={props.errorMessage}
            languageUX={props.languageUX}
            searchedItemListWithKey={props.searchedItemListWithKey}
            divisionOptions={divisionOptions}
          />
        );
      }
      case "TrainingManagement": {
        return (
          <CardTrainingManagement
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            TrainingManagementSequence={props.TrainingManagementSequence}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
            searchedItemListWithKey={props.searchedItemListWithKey}
            divisionOptions={divisionOptions}
          />
        );
      }
      case "Attendance": {
        return (
          <CardEmployeeTimeAttendance
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            EmployeeTASequence={props.EmployeeTASequence}
            buttonLoadCheck={props.buttonLoadCheck}
            searchedItemListWithKey={props.searchedItemListWithKey}
            errorMessage={props.errorMessage}
            successMessage={props.successMessage}
            masterOptions={props.masterOptions}
            divisionOptions={divisionOptions}
            languageUX={props.languageUX}
          />
        );
      }
      case "Summary": {
        return (
          <CardEmployeeSummaryWorkHour
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            EmployeeSWHSequence={props.EmployeeSWHSequence}
            buttonLoadCheck={props.buttonLoadCheck}
            errorMessage={props.errorMessage}
            languageUX={props.languageUX}
            masterOptions={props.masterOptions}
            divisionOptions={divisionOptions}
          />
        );
      }
      default:
        return null;
    }
  }, [
    activeTab,
    divisionOptions,
    props.adminMenuDivisions,
    props.buttonLoadCheck,
    props.CourseListSequence,
    props.django,
    props.EmployeeSWHSequence,
    props.EmployeeTASequence,
    props.errorMessage,
    props.languageUX,
    props.masterData,
    props.masterOptions,
    props.searchedItemListWithKey,
    props.selectedDivision,
    props.SetupWorkScheduleSequence,
    props.successMessage,
    props.TrainingManagementSequence,
    props.WorkScheduleSequence,
  ]);

  return (
    <>
      <SnackMessage
        // func
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_NAME}`, null);
          props.setProp(`successMessage.${CARD_NAME}`, null);
        }}
        error={props.errorMessage?.[CARD_NAME]}
        success={props.successMessage?.[CARD_NAME]}
        languageUX={props.languageUX}
      />

      <CardWorkScheduleUX
        activeTab={activeTab}
        displayTab={displayTab}
        filteredTabMenu={filteredTabMenu}
        onHandleChangeTab={handleChangeTab}
      />
    </>
  );
};

export default CardWorkSchedule;
